/**
 * Ambientes ÓRFÃOS: o Mapa guarda alocação, tarefa, peça, comentário do
 * histórico (o mesmo das páginas do Legado), projetista do executivo ou marca
 * de carga da Capacidade da Fábrica com uma chave
 * ("PEDIDO:AMBIENTE") que a planilha não tem mais — quase sempre porque o
 * PPCP renomeou o ambiente (plano §P2). Some da planta sem aviso; esta
 * lista é o que a tela oferece para religar (`conciliacao.religar`).
 */
import { SEPARADOR_CHAVE, chaveAmbiente, rotuloAmbiente } from "./chaves";

const pedidoDaChave = (chave) => String(chave || "").split(SEPARADOR_CHAVE)[0];

// O que é "tem ou não tem" (os demais campos são contagens).
const MARCAS = new Set(["alocacao", "projetista", "excecao"]);

function acumular(porChave, chave, rotulo, campo, quantos = 1) {
  if (!chave) return;
  const atual = porChave.get(chave) || {
    chave,
    rotulo: "",
    alocacao: false,
    tarefas: 0,
    pecas: 0,
    comentarios: 0,
    projetista: false,
    excecao: false,
  };
  atual.rotulo = atual.rotulo || rotulo || "";
  if (MARCAS.has(campo)) atual[campo] = true;
  else atual[campo] += quantos;
  porChave.set(chave, atual);
}

/** Chaves de todas as linhas da planilha (inclusive as já carregadas). */
export function chavesDaPlanilha(linhas = []) {
  return new Set(linhas.map(chaveAmbiente).filter(Boolean));
}

/**
 * `[{chave, rotulo, alocacao, tarefas, pecas, comentarios, projetista, excecao}]`
 * do que o sistema guarda e a planilha não conhece. `comentarios` =
 * `{chave atual: total}` da contagem do histórico; `projetistas` e `excecoes`
 * = `{chave atual: ...}` de `useProjetistasExecutivo` e `useExcecoesCapacidade`
 * (todos já seguem as religações). Sem planilha carregada, nada é órfão
 * (tudo seria).
 *
 * Projetista e marca de carga entram porque um ambiente que só tem isso
 * também se perde quando é renomeado: sem aparecer aqui, ninguém religa.
 */
export function ambientesOrfaos(estado = {}, chaves = new Set(), comentarios = {}, { projetistas = {}, excecoes = {} } = {}) {
  if (chaves.size === 0) return [];
  const porChave = new Map();
  (estado.alocacoes || []).forEach((a) => acumular(porChave, a.chave, a.rotulo, "alocacao"));
  (estado.tarefas || []).forEach((t) => acumular(porChave, t.ambiente_chave, t.ambiente_rotulo, "tarefas"));
  (estado.pecas || []).forEach((p) => acumular(porChave, p.ambiente_chave, p.ambiente_rotulo, "pecas"));
  Object.entries(comentarios || {}).forEach(([chave, total]) =>
    acumular(porChave, chave, "", "comentarios", Number(total) || 0)
  );
  Object.keys(projetistas || {}).forEach((chave) => acumular(porChave, chave, "", "projetista"));
  Object.keys(excecoes || {}).forEach((chave) => acumular(porChave, chave, "", "excecao"));
  return [...porChave.values()]
    .filter((orfao) => !chaves.has(orfao.chave))
    .sort((a, b) => a.chave.localeCompare(b.chave, "pt-BR"));
}

/**
 * Ambientes da planilha para onde o órfão pode ir: os do MESMO pedido
 * primeiro (renomeação é o caso comum), sem os que já têm alocação — religar
 * não junta dois ambientes. `[{chave, rotulo, mesmoPedido}]`.
 */
export function candidatosPara(orfao, linhas = [], estado = {}) {
  const ocupadas = new Set((estado.alocacoes || []).map((a) => a.chave));
  const pedido = pedidoDaChave(orfao.chave);
  const vistos = new Set();
  const candidatos = [];
  linhas.forEach((row) => {
    const chave = chaveAmbiente(row);
    if (!chave || vistos.has(chave) || (orfao.alocacao && ocupadas.has(chave))) return;
    vistos.add(chave);
    candidatos.push({ chave, rotulo: rotuloAmbiente(row), mesmoPedido: pedidoDaChave(chave) === pedido });
  });
  return candidatos.sort(
    (a, b) => Number(b.mesmoPedido) - Number(a.mesmoPedido) || a.chave.localeCompare(b.chave, "pt-BR"),
  );
}
